export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center">
            <div className="text-6xl mb-6">👋</div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Hello.Reviews
            </h1>
            <p className="text-2xl md:text-3xl text-green-50 mb-6 max-w-3xl mx-auto">
              Your happy customers want to help. Let them—
            </p>
            <p className="text-xl md:text-2xl text-green-100 mb-12 max-w-3xl mx-auto font-semibold">
              on Google, where their reviews actually get seen.
            </p>
            <button className="bg-white text-green-600 font-bold text-lg px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
              Get in Touch
            </button>
          </div>
        </div>
      </div>

      {/* Value Props Grid */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Reviews are ❤️ */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">❤️</div>
            <h3 className="text-2xl font-bold mb-4">Reviews are ❤️</h3>
            <p className="text-gray-600 leading-relaxed">
              Every 5-star review is a customer saying "I trust you enough to tell others." That's gold.
            </p>
          </div>

          {/* Timing is Important */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">⏰</div>
            <h3 className="text-2xl font-bold mb-4">Timing is Important</h3>
            <p className="text-gray-600 leading-relaxed">
              Ask too soon? They're still annoyed. Too late? They forgot. We ask at exactly the right moment.
            </p>
          </div>

          {/* Make It Simple */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">✨</div>
            <h3 className="text-2xl font-bold mb-4">Make It Simple</h3>
            <p className="text-gray-600 leading-relaxed">
              No logins. No apps. Just a text asking "How'd we do?" and a link if they want to share.
            </p>
          </div>

          {/* Put Out Fires */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">🚒</div>
            <h3 className="text-2xl font-bold mb-4">Put Out Fires</h3>
            <p className="text-gray-600 leading-relaxed">
              Unhappy customer? They tell you privately first. You fix it before it hits Google.
            </p>
          </div>

          {/* Make Them Smile */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">😊</div>
            <h3 className="text-2xl font-bold mb-4">Make Them Smile</h3>
            <p className="text-gray-600 leading-relaxed">
              When customers share their story, they relive the good parts. That's free marketing.
            </p>
          </div>

          {/* Watch Your Results */}
          <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-6">📊</div>
            <h3 className="text-2xl font-bold mb-4">Watch Your Results</h3>
            <p className="text-gray-600 leading-relaxed">
              See your rating climb. Track happy customers. Know what's working.
            </p>
          </div>
        </div>
      </div>

      {/* Not Testimonials Section */}
      <div className="bg-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            This Isn't About Testimonials on Your Website
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-4">
            Most businesses get fewer than 100 visitors per month. Beautiful testimonial widgets nobody sees? That's not our game.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed font-semibold">
            We put your reviews on <span className="text-blue-600">Google</span>, <span className="text-blue-600">Yelp</span>, and <span className="text-blue-600">Facebook</span>—where your next customers are already searching.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">The Reputation Growth Engine</h2>

          <div className="space-y-12">
            {/* Step 1 */}
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-3">We Collect Reviews</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Every week, we reach out to your happy customers via SMS and email.
                  AI picks the perfect timing and writes personalized messages.
                  You approve, we send. Simple.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-3">We Route Feedback</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Happy customer? They leave a <strong>public review on Google, Yelp, or Facebook</strong>—where 87% of customers search before choosing a local business.
                  Unhappy? Private feedback form, you fix it first before it hits the internet.
                  AI handles the sorting automatically.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-3">We Activate Reviews</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  One review becomes 10 marketing touches.
                  We turn it into social posts, newsletter content, website testimonials, email signatures.
                  You get the content, ready to use.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-3">You See Results</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Monthly report shows: reviews collected, rating improvement, competitor comparison.
                  30-minute call to celebrate wins and plan next month.
                  You focus on your business, we handle reputation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Choose Your Path</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            Start with service, grow into software. We meet you where you are.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Done-For-You */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-4 border-green-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                MOST POPULAR
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Done-For-You</h3>
                <div className="text-4xl font-bold text-green-600 mb-4">$299<span className="text-xl text-gray-500">/mo</span></div>
                <p className="text-gray-600 mb-6">We collect reviews for you</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Weekly review outreach</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">AI-powered timing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Response management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Content creation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Monthly reporting</span>
                  </li>
                </ul>
                <button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg transition-colors">
                  Let's Talk
                </button>
              </div>
            </div>

            {/* Setup Help */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Setup Help</h3>
                <div className="text-4xl font-bold text-gray-900 mb-4">$299<span className="text-xl text-gray-500"> once</span></div>
                <p className="text-gray-600 mb-6">We set it up, you run it</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Complete configuration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Workflow automation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Team training</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">30 days email support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Best practices guide</span>
                  </li>
                </ul>
                <button className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-lg transition-colors">
                  Get Started
                </button>
              </div>
            </div>

            {/* DIY */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">DIY Software</h3>
                <div className="text-4xl font-bold text-gray-900 mb-4">$49<span className="text-xl text-gray-500">/mo</span></div>
                <p className="text-gray-600 mb-6">Self-service platform</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Review collection tools</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Automated workflows</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Public review pages</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Basic analytics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Email support</span>
                  </li>
                </ul>
                <button className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-lg transition-colors">
                  Start Free Trial
                </button>
              </div>
            </div>

            {/* White-Label */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">White-Label</h3>
                <div className="text-4xl font-bold text-gray-900 mb-4">$999<span className="text-xl text-gray-500">/mo</span></div>
                <p className="text-gray-600 mb-6">Resell to your clients</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Custom branding</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Agency dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Unlimited clients</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Priority support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">Revenue share optional</span>
                  </li>
                </ul>
                <button className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-lg transition-colors">
                  Partner With Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-green-50 mb-10 leading-relaxed">
            Join businesses using Hello.Reviews to build trust and grow.
          </p>
          <button className="bg-white text-green-600 font-bold text-lg px-10 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
            Get in Touch
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="text-2xl font-bold">Hello.Reviews</div>
              <p className="text-gray-400 text-sm mt-1">Your reputation growth engine</p>
            </div>
            <div className="flex flex-wrap gap-6 text-gray-400">
              <a href="/#dropstuff" className="hover:text-white transition-colors">Dropstuff</a>
              <a href="/#work" className="hover:text-white transition-colors">Work</a>
              <a href="/#research" className="hover:text-white transition-colors">Research</a>
              <a href="/#privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/#terms" className="hover:text-white transition-colors">Terms</a>
              <a href="mailto:hello@hello.reviews" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
